import { useCallback, useEffect, useState } from "react";
import { useSession } from "@/auth/session-provider";
import { useTopbarIsland } from "@/features/workbench/topbar-island-context";
import { useI18n } from "@/locales/i18n-provider";
import { useNotifications } from "./notification-provider";

const DISMISSAL_DURATION = 7 * 24 * 60 * 60 * 1_000;
const FEEDBACK_DURATION = 4_000;
const OPERATION_ID = "webPushPermission";

type IslandFeedback = {
  description?: string;
  title: string;
  tone: "error" | "success";
};

function dismissalStorageKey(agentId: string) {
  return `tickbird:web-push-onboarding:dismissed-until:${agentId}`;
}

function completionStorageKey(agentId: string) {
  return `tickbird:web-push-onboarding:completed:${agentId}`;
}

function isDismissed(agentId: string) {
  try {
    const dismissedUntil = Number.parseInt(window.localStorage.getItem(dismissalStorageKey(agentId)) ?? "0", 10);
    return Number.isSafeInteger(dismissedUntil) && dismissedUntil > Date.now();
  } catch {
    return false;
  }
}

function hasCompletedOnboarding(agentId: string) {
  try {
    return window.localStorage.getItem(completionStorageKey(agentId)) === "true";
  } catch {
    return false;
  }
}

function markOnboardingCompleted(agentId: string) {
  try {
    window.localStorage.setItem(completionStorageKey(agentId), "true");
  } catch {
    // The in-memory state still prevents another prompt during the current session.
  }
}

type WebPushOnboardingProps = {
  suppressed?: boolean;
};

export function WebPushOnboarding({ suppressed = false }: WebPushOnboardingProps) {
  const { agent } = useSession();
  const { webPush } = useNotifications();
  const { dismiss: dismissTopbarIsland, show: showTopbarIsland } = useTopbarIsland();
  const { t } = useI18n();
  const [dismissed, setDismissed] = useState(() => isDismissed(agent.id));
  const [completed, setCompleted] = useState(() => hasCompletedOnboarding(agent.id));
  const [dismissedForSession, setDismissedForSession] = useState(false);
  const [enableStartedFromIsland, setEnableStartedFromIsland] = useState(false);
  const [islandFeedback, setIslandFeedback] = useState<IslandFeedback | null>(null);

  useEffect(() => {
    setDismissed(isDismissed(agent.id));
    setCompleted(hasCompletedOnboarding(agent.id));
    setDismissedForSession(false);
    setEnableStartedFromIsland(false);
    setIslandFeedback(null);
  }, [agent.id]);

  useEffect(() => {
    if (suppressed) {
      setDismissedForSession(true);
    }
  }, [suppressed]);

  useEffect(() => {
    if (webPush.state.status === "subscribed") {
      markOnboardingCompleted(agent.id);
      setCompleted(true);
    }
  }, [agent.id, webPush.state.status]);

  const dismiss = useCallback(() => {
    try {
      window.localStorage.setItem(dismissalStorageKey(agent.id), String(Date.now() + DISMISSAL_DURATION));
    } catch {
      // The current session still remains dismissed when storage is unavailable.
    }
    setDismissed(true);
  }, [agent.id]);

  const deliberatelyDisabled = webPush.state.lastCompletedAction === "disable";

  useEffect(() => {
    if (deliberatelyDisabled) {
      dismiss();
    }
  }, [deliberatelyDisabled, dismiss]);

  const enableFromIsland = useCallback(() => {
    setIslandFeedback(null);
    setEnableStartedFromIsland(true);
    void webPush.enable({ feedback: "silent" });
  }, [webPush.enable]);

  useEffect(() => {
    if (!enableStartedFromIsland || webPush.state.pendingAction === "enable") {
      return;
    }

    if (webPush.state.status === "subscribed") {
      setIslandFeedback({
        title: t("notifications.push.enabledSuccess"),
        tone: "success",
      });
      setEnableStartedFromIsland(false);
      return;
    }

    if (webPush.state.status === "unsubscribed") {
      setEnableStartedFromIsland(false);
      return;
    }

    const feedback =
      webPush.state.status === "denied"
        ? {
            description: t("notifications.push.deniedDescription"),
            title: t("notifications.push.deniedTitle"),
            tone: "error" as const,
          }
        : webPush.state.status === "server-disabled"
          ? {
              description: t("notifications.push.serverDisabledDescription"),
              title: t("notifications.push.serverDisabledTitle"),
              tone: "error" as const,
            }
          : webPush.state.status === "unsupported"
            ? {
                description: t("notifications.push.unsupportedDescription"),
                title: t("notifications.push.unsupportedTitle"),
                tone: "error" as const,
              }
            : {
                description: t("notifications.push.errorDescription"),
                title: t("notifications.push.updateFailed"),
                tone: "error" as const,
              };

    setIslandFeedback(feedback);
    setEnableStartedFromIsland(false);
  }, [enableStartedFromIsland, t, webPush.state.pendingAction, webPush.state.status]);

  useEffect(() => {
    if (!islandFeedback) {
      return;
    }

    const timer = window.setTimeout(() => setIslandFeedback(null), FEEDBACK_DURATION);
    return () => window.clearTimeout(timer);
  }, [islandFeedback]);

  const canEnable = webPush.state.status === "unsubscribed" || (webPush.state.status === "error" && webPush.state.failedAction === "enable");
  const externallyEnabling = !enableStartedFromIsland && webPush.state.pendingAction === "enable";
  const open = canEnable && !completed && !deliberatelyDisabled && !dismissed && !dismissedForSession && !suppressed && !externallyEnabling;

  useEffect(() => {
    if (islandFeedback) {
      showTopbarIsland(
        "operation",
        {
          ...islandFeedback,
          id: OPERATION_ID,
          phase: "result",
        },
        { duration: FEEDBACK_DURATION },
      );
      return;
    }

    if (enableStartedFromIsland) {
      showTopbarIsland(
        "operation",
        {
          description: t("notifications.push.enablingDescription"),
          id: OPERATION_ID,
          phase: "loading",
          title: t("notifications.push.enabling"),
        },
        { duration: null },
      );
      return;
    }

    if (!open) {
      dismissTopbarIsland(OPERATION_ID);
      return;
    }

    showTopbarIsland(
      "operation",
      {
        cancelLabel: t("notifications.push.later"),
        confirmLabel: t("notifications.push.onboardingEnable"),
        description: t("notifications.push.onboardingDescription"),
        icon: "line-md:bell-alert-twotone-loop",
        id: OPERATION_ID,
        onCancel: dismiss,
        onConfirm: enableFromIsland,
        phase: "prompt",
        title: t("notifications.push.onboardingTitle"),
      },
      { duration: null },
    );
  }, [dismiss, dismissTopbarIsland, enableFromIsland, enableStartedFromIsland, islandFeedback, open, showTopbarIsland, t]);

  useEffect(() => () => dismissTopbarIsland(OPERATION_ID), [dismissTopbarIsland]);

  return null;
}
